import { useEffect, useId, useRef, useState, type CSSProperties } from 'react';
import { Loader2, X } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Checkbox } from '@/components/atoms/checkbox';
import { Dialog, DialogContent, DialogTitle } from '@/components/atoms/dialog';
import SignaturePad, { type SignaturePadHandle } from '@/components/molecules/SignaturePad';
import { CustomerTermsBody } from '@/components/public/CustomerTermsBody';
import type { ResolvedCustomerTerms } from '@/lib/terms/clientTerms';

const fmt = (n: number) =>
  `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** The customer's signed acceptance for one option. */
export interface ProposalApproveSignature {
  name: string;
  image: string;
}

/** The totals recap the modal prints, computed from the active option. */
export interface ProposalApproveRecap {
  scopeLabel: string;
  requiredSubtotal: number;
  optionalRows: Array<{ name: string; subtotal: number }>;
  membershipSavings: number;
  membershipSavingsLabel?: string | null;
  membershipPlanName?: string | null;
  membershipPlanPrice?: number | null;
  membershipPlanInterval?: string | null;
  membershipSetupFee?: number;
  discount: number;
  tax: number;
  total: number;
  depositDue: number;
}

/**
 * The single-scope proposal approve flow — the SAME modal the individual estimate
 * opens from its floating action pill: a totals recap, the signature pad, an
 * authorization clause (with the terms expandable inline), and a Cancel /
 * "Approve $X" footer. Opened from the page-level action bar (outside the option
 * tabs); approving an option IS selecting it. Themed by the design branding.
 */
export function ProposalApproveModal({
  open,
  onOpenChange,
  proposalNumber,
  companyName,
  customerName,
  signatureRequired,
  terms,
  recap,
  submitting,
  themeVars,
  onApprove,
  onAcceptanceChange,
  submitLabel,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  proposalNumber?: string | null;
  companyName?: string | null;
  customerName?: string | null;
  signatureRequired: boolean;
  terms: ResolvedCustomerTerms;
  recap: ProposalApproveRecap;
  submitting: boolean;
  themeVars?: CSSProperties;
  onApprove: (signature: ProposalApproveSignature | null) => void;
  onAcceptanceChange?: (accepted: boolean) => void;
  /** Replaces the default "Approve $X" — "Approve and pay" when a deposit is due. */
  submitLabel?: string;
}) {
  const [sigName, setSigName] = useState(customerName ?? '');
  const [sigHasInk, setSigHasInk] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const sigPadRef = useRef<SignaturePadHandle>(null);
  const acceptanceId = useId();

  const signatureReady = !signatureRequired || (sigName.trim().length > 0 && sigHasInk);
  const canApprove = accepted && signatureReady && !submitting;

  // Reset every closing path — Cancel/X/Approve all close by flipping `open`
  // through the parent, which never runs the Dialog's own onOpenChange. So key
  // off `open` itself: whenever the modal is closed, clear consent + signature so
  // reopening (for a different option/amount) never shows a pre-ticked
  // authorization. Restore the customer's name as the editable default.
  useEffect(() => {
    if (!open) {
      setSigHasInk(false);
      setShowTerms(false);
      setAccepted(false);
      setSigName(customerName ?? '');
      onAcceptanceChange?.(false);
    }
  }, [open, onAcceptanceChange, customerName]);

  const submit = () => {
    const image = sigPadRef.current?.getDataURL() ?? null;
    const name = sigName.trim();
    onApprove(image && name ? { name, image } : null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="flex max-h-[92vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-[540px]"
        style={themeVars}
      >
        <div className="flex shrink-0 items-center gap-3 border-b border-[#14141A]/[0.08] px-5 py-[18px]">
          <DialogTitle className="min-w-0 flex-1 text-[17px] font-medium tracking-[-0.02em] text-[#17171A]">
            Approve proposal{proposalNumber ? ` ${proposalNumber}` : ''}
          </DialogTitle>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            aria-label="Close"
            className="flex size-9 shrink-0 items-center justify-center rounded-md border border-[#14141A]/[0.13] text-[#63646B] transition-colors hover:border-[#14141A]/[0.30] hover:text-[#17171A]"
          >
            <X className="size-[17px]" strokeWidth={1.8} />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-5">
          {/* Totals recap. */}
          <div className="rounded-[10px] border border-[#14141A]/[0.08] bg-[#FBFBFA] px-[21px] py-[19px]">
            <div className="flex justify-between gap-4 py-2 text-[14.5px]">
              <span className="min-w-0 text-[#63646B]">{recap.scopeLabel}</span>
              <span className="shrink-0 font-medium tabular-nums text-[#17171A]">
                {fmt(recap.requiredSubtotal)}
              </span>
            </div>
            {recap.optionalRows.map((row, i) => (
              <div
                key={`recap-opt-${i}`}
                className="flex justify-between gap-4 border-t border-[#0A0A0A]/[0.045] py-2 text-[14.5px]"
              >
                <span className="min-w-0 text-[#63646B]">{row.name}</span>
                <span className="shrink-0 font-medium tabular-nums text-[#17171A]">
                  {fmt(row.subtotal)}
                </span>
              </div>
            ))}
            {recap.discount > 0 ? (
              <div className="flex justify-between gap-4 border-t border-[#0A0A0A]/[0.045] py-2 text-[14.5px]">
                <span className="min-w-0 text-[#63646B]">Discount</span>
                <span className="shrink-0 font-medium tabular-nums text-[#17171A]">
                  -{fmt(recap.discount)}
                </span>
              </div>
            ) : null}
            {recap.membershipSavings > 0 ? (
              <div className="flex justify-between gap-4 border-t border-[#0A0A0A]/[0.045] py-2 text-[14.5px]">
                <span className="min-w-0 text-emerald-600">
                  Membership savings
                  {recap.membershipSavingsLabel ? ` (${recap.membershipSavingsLabel})` : ''}
                </span>
                <span className="shrink-0 font-medium tabular-nums text-emerald-600">
                  -{fmt(recap.membershipSavings)}
                </span>
              </div>
            ) : null}
            {recap.membershipPlanPrice != null && recap.membershipPlanPrice > 0 ? (
              <div className="flex justify-between gap-4 border-t border-[#0A0A0A]/[0.045] py-2 text-[14.5px]">
                <span className="min-w-0 text-[#63646B]">
                  {recap.membershipPlanName ?? 'Membership'}
                </span>
                <span className="shrink-0 font-medium tabular-nums text-[#17171A]">
                  {fmt(recap.membershipPlanPrice)}
                  {recap.membershipPlanInterval}
                </span>
              </div>
            ) : null}
            {!!recap.membershipSetupFee && (
              <div className="flex justify-between gap-4 py-2 text-sm">
                <span>One-time setup fee</span>
                <span>{fmt(recap.membershipSetupFee)}</span>
              </div>
            )}
            {recap.tax > 0 ? (
              <div className="flex justify-between gap-4 border-t border-[#0A0A0A]/[0.045] py-2 text-[14.5px]">
                <span className="text-[#63646B]">Sales tax</span>
                <span className="shrink-0 font-medium tabular-nums text-[#17171A]">
                  {fmt(recap.tax)}
                </span>
              </div>
            ) : null}
            <div className="mt-[13px] flex items-baseline justify-between gap-4 border-t border-[#14141A]/[0.13] pt-[15px]">
              <span className="text-[15px] font-medium text-[#17171A]">Total</span>
              <b className="text-[29px] font-medium leading-none tracking-[-0.032em] tabular-nums text-[#17171A]">
                {fmt(recap.total)}
              </b>
            </div>
            {recap.depositDue > 0 ? (
              <div className="mt-2 flex justify-between gap-4 text-[13px]">
                <span className="text-[#63646B]">Deposit due today</span>
                <span className="shrink-0 tabular-nums text-[#374151]">
                  {fmt(recap.depositDue)}
                </span>
              </div>
            ) : null}
          </div>

          {/* Signature. */}
          {signatureRequired ? (
            <div className="mt-6">
              <span className="mb-1.5 block text-[13px] font-medium text-[#63646B]">
                Sign to approve
              </span>
              <div className="overflow-hidden rounded-[10px] border border-[#14141A]/[0.13] bg-white">
                <div className="relative h-[150px]">
                  <SignaturePad
                    ref={sigPadRef}
                    unstyled
                    height={150}
                    onBegin={() => setSigHasInk(true)}
                  />
                  <div className="pointer-events-none absolute inset-x-[26px] bottom-[38px] border-t border-[#14141A]/[0.13]" />
                  {!sigHasInk ? (
                    <span className="pointer-events-none absolute bottom-[18px] left-[26px] text-[12.5px] text-[#95969D]">
                      Draw your signature here
                    </span>
                  ) : null}
                </div>
                <div className="flex items-center gap-3 border-t border-[#14141A]/[0.08] bg-[#FBFBFA] px-[14px] py-2.5">
                  <button
                    type="button"
                    onClick={() => {
                      sigPadRef.current?.clear();
                      setSigHasInk(false);
                    }}
                    className="text-[13px] font-medium text-[var(--ss-accent-strong)] hover:underline"
                  >
                    Clear
                  </button>
                  <input
                    type="text"
                    placeholder="Your name"
                    value={sigName}
                    onChange={(e) => setSigName(e.target.value)}
                    aria-label="Signature name"
                    className="min-w-0 flex-1 bg-transparent text-right text-[13px] text-[#63646B] outline-none placeholder:text-[#95969D]"
                  />
                </div>
              </div>
            </div>
          ) : null}

          {/* Authorization clause. */}
          <div className="mt-5 flex items-start gap-[11px] text-[14.5px] leading-[1.45] text-[#374151]">
            <Checkbox
              id={acceptanceId}
              checked={accepted}
              onCheckedChange={(v) => {
                setAccepted(v === true);
                onAcceptanceChange?.(v === true);
              }}
              className="mt-0.5"
            />
            <span>
              <label htmlFor={acceptanceId} className="cursor-pointer">
                I authorize {companyName ?? 'the company'} to carry out the work described in this
                proposal
                {terms.hasTerms
                  ? ' and I accept the terms in this proposal.'
                  : ' and approve this proposal.'}
              </label>{' '}
              {terms.hasTerms ? (
                <button
                  type="button"
                  aria-expanded={showTerms}
                  onClick={() => setShowTerms((s) => !s)}
                  className="font-medium text-[var(--ss-accent-strong)] underline underline-offset-2 hover:text-[#17171A]"
                >
                  View terms
                </button>
              ) : null}
            </span>
          </div>

          {terms.hasTerms && showTerms ? (
            <div className="mt-3 max-h-56 overflow-y-auto rounded-[10px] border border-[#14141A]/[0.08] bg-[#FBFBFA] px-4 py-3">
              <CustomerTermsBody
                terms={terms}
                className="text-[13px] leading-[1.6] text-[#63646B]"
              />
            </div>
          ) : null}
        </div>

        <div className="flex shrink-0 gap-2.5 border-t border-[#14141A]/[0.08] bg-[#FBFBFA] px-5 py-[15px]">
          <Button
            variant="outline"
            className="h-11 flex-1 text-[15px] font-medium"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            className="h-11 flex-1 bg-[var(--ss-accent)] text-[15px] font-medium text-white hover:bg-[var(--ss-accent-strong)]"
            onClick={submit}
            disabled={!canApprove}
          >
            {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {submitLabel ?? `Approve ${fmt(recap.total)}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
